import { useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  SORT_MODES,
  SORT_MODE_LABEL,
  sortLines,
  type SortMode,
} from '../lib/sortLines';

const SAMPLE_INPUT = [
  'file10.png',
  'file2.png',
  'file1.png',
  'README.md',
  'readme.MD',
  '中文',
  '排序',
  '',
  'foo',
].join('\n');

/**
 * `/tools/convert/sort-lines` — line sorter.
 *
 * Single textarea on top, 6 sort-mode pills + 3 toggles, output
 * textarea below with a copy button. Pure compute, no debounce
 * needed for inputs up to ~10k lines (Intl.Collator is fast).
 */
export default function SortLinesTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLE_INPUT);
  const [mode, setMode] = useState<SortMode>('asc');
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [trim, setTrim] = useState(false);
  const [ignoreEmpty, setIgnoreEmpty] = useState(false);
  const [copied, setCopied] = useState(false);

  const out = useMemo(
    () => sortLines(input, mode, { caseInsensitive, trim, ignoreEmpty }),
    [input, mode, caseInsensitive, trim, ignoreEmpty],
  );

  const onCopy = async (): Promise<void> => {
    if (!out.result) return;
    try {
      await navigator.clipboard.writeText(out.result);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* clipboard write may fail on insecure context */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="sort-lines"
        category="convert"
        description="按行排序：6 种模式（升 / 降 / 长度升 / 长度降 / 自然排序 / 反转）+ 大小写不敏感 + 去空 + trim。自然排序基于 Intl.Collator numeric，能正确处理 file2 < file10。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            模式
          </span>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {SORT_MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={clsx(
                'rounded border px-2 py-0.5 text-[12px] transition',
                m === mode
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
              )}
            >
              {SORT_MODE_LABEL[m]}
            </button>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-ink-muted">
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={caseInsensitive}
              onChange={(e) => setCaseInsensitive(e.target.checked)}
            />
            大小写不敏感
          </label>
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={trim}
              onChange={(e) => setTrim(e.target.checked)}
            />
            自动 trim 每行
          </label>
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={ignoreEmpty}
              onChange={(e) => setIgnoreEmpty(e.target.checked)}
            />
            忽略空行
          </label>
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入
          </span>
          <span className="text-[11px] text-ink-dim">
            {input ? input.split(/\r?\n/).length : 0} 行
          </span>
        </header>
        <textarea
          className="input min-h-[140px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder="每行一项，下方实时显示排序结果"
        />
      </section>

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输出
          </span>
          <div className="flex items-center gap-2 text-[11px] text-ink-dim">
            <span>{out.lineCount} 行</span>
            {out.droppedEmpty > 0 && (
              <span className="badge">忽略 {out.droppedEmpty} 空行</span>
            )}
            <button
              type="button"
              onClick={onCopy}
              disabled={!out.result}
              className="btn-ghost px-2 py-0.5"
              title="复制结果"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  已复制
                </>
              ) : (
                <>
                  <ClipboardCopy className="h-3 w-3" />
                  复制
                </>
              )}
            </button>
          </div>
        </header>
        <textarea
          className="input flex-1 resize-none font-mono text-[13px]"
          value={out.result}
          readOnly
          spellCheck={false}
        />
      </section>
    </div>
  );
}
